import React, { useState, useEffect } from 'react';
import { Search, CheckCircle, Clock, Play, AlertCircle, X, Eye, ChevronRight, User } from 'lucide-react';
import { API_URL } from '../../services/api';

function SuiviFicheChef() {
  const [ordres, setOrdres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrdre, setSelectedOrdre] = useState(null);
  const [ficheData, setFicheData] = useState(null);
  const [ficheLoading, setFicheLoading] = useState(false);
  const [validating, setValidating] = useState(null);

  useEffect(() => {
    loadOrdres();
    const interval = setInterval(loadOrdres, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadOrdres = async () => {
    try {
      const res = await fetch(`${API_URL}/atelier/ordres-disponibles`);
      if (res.ok) {
        const data = await res.json();
        setOrdres(data);
      }
    } catch (e) {
      console.error('Erreur chargement ordres:', e);
    } finally {
      setLoading(false);
    }
  };

  const ouvrirFiche = async (ordre) => {
    setSelectedOrdre(ordre);
    setFicheLoading(true);
    setFicheData(null);
    try {
      const [ficheRes, etapesRes] = await Promise.all([
        fetch(`${API_URL}/atelier/fiche-fabrication/${ordre.id}`),
        fetch(`${API_URL}/atelier/fabrication/${ordre.id}/etapes`)
      ]);
      const fiche = ficheRes.ok ? await ficheRes.json() : null;
      const etapesData = etapesRes.ok ? await etapesRes.json() : null;

      const etapesProduction = etapesData?.etapes_production || [];
      const etapesDevis = etapesData?.etapes_devis || [];

      // Map production par nom_etape pour accéder rapidement aux données réelles (ouvrier, dates, id)
      const prodMap = {};
      for (const ep of etapesProduction) {
        prodMap[ep.nom_etape] = ep;
      }

      // Base = toutes les étapes du devis (elles sont en vert)
      // + étapes de production qui ne sont pas dans le devis (ajoutées manuellement)
      const etapesDevisNoms = new Set(etapesDevis.map(e => e.nom_etape));

      const etapesFinales = [
        // 1. Étapes du devis — avec données production si lancées
        ...etapesDevis.map(ed => {
          const prod = prodMap[ed.nom_etape];
          if (prod) {
            return { ...prod, in_devis: true, source: 'production' };
          }
          return { ...ed, in_devis: true, source: 'devis_only', statut: 'non_demarre' };
        }),
        // 2. Étapes production hors devis (ajoutées manuellement via ouvrier)
        ...etapesProduction
          .filter(ep => !etapesDevisNoms.has(ep.nom_etape))
          .map(ep => ({ ...ep, in_devis: false, source: 'production' }))
      ];

      setFicheData({ ...fiche, etapes_production: etapesFinales });
    } catch (e) {
      console.error('Erreur chargement fiche:', e);
    } finally {
      setFicheLoading(false);
    }
  };

  const validerEtape = async (etapeId, nomEtape) => {
    if (!window.confirm(`Valider l'étape "${nomEtape}" ?`)) return;
    setValidating(etapeId);
    try {
      const res = await fetch(`${API_URL}/atelier/etapes-production/${etapeId}/valider`, {
        method: 'POST'
      });
      if (res.ok) {
        await ouvrirFiche(selectedOrdre);
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.error}`);
      }
    } catch (e) {
      alert('Erreur réseau');
    } finally {
      setValidating(null);
    }
  };

  const terminerOF = async () => {
    if (!selectedOrdre) return;
    if (!window.confirm(`Marquer l'OF ${selectedOrdre.numero_ordre} comme TERMINÉ ?\nLe bijou sera prêt pour le stock.`)) return;
    try {
      const res = await fetch(`${API_URL}/atelier/ordres-fabrication/${selectedOrdre.id}/terminer-of`, {
        method: 'POST'
      });
      if (res.ok) {
        setSelectedOrdre(null);
        setFicheData(null);
        await loadOrdres();
      } else {
        const err = await res.json();
        alert(`Erreur: ${err.error}`);
      }
    } catch (e) {
      alert('Erreur réseau');
    }
  };

  const getStatutStyle = (statut) => {
    switch (statut) {
      case 'validee':   return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', label: '✓ Validée', icon: <CheckCircle size={14} className="text-green-600" /> };
      case 'terminee':  return { bg: 'bg-blue-100',  text: 'text-blue-800',  border: 'border-blue-300',  label: '⏸ Terminée', icon: <CheckCircle size={14} className="text-blue-600" /> };
      case 'en_cours':  return { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', label: '⚙ En cours', icon: <Play size={14} className="text-orange-600" /> };
      case 'en_attente':  return { bg: 'bg-gray-100',  text: 'text-gray-600',  border: 'border-gray-300',  label: '○ En attente',    icon: <Clock size={14} className="text-gray-400" /> };
      case 'non_demarre': return { bg: 'bg-green-50',  text: 'text-green-700', border: 'border-green-200', label: '📋 À planifier',  icon: <CheckCircle size={14} className="text-green-300" /> };
      default:            return { bg: 'bg-gray-50',   text: 'text-gray-500',  border: 'border-gray-200',  label: statut,             icon: null };
    }
  };

  const ordresFiltres = ordres.filter(o =>
    o.numero_ordre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.type_bijou || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.client_nom || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const etapesEnAttente  = ficheData?.etapes_production?.filter(e => e.statut === 'terminee').length || 0;
  const etapesValidees   = ficheData?.etapes_production?.filter(e => e.statut === 'validee').length || 0;
  const totalEtapes      = ficheData?.etapes_production?.length || 0;
  const etapesNonDemarrees = ficheData?.etapes_production?.filter(e => e.statut === 'non_demarre').length || 0;
  const etapesActives    = totalEtapes - etapesNonDemarrees;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Eye className="text-indigo-600" />
          Suivi Fiches — Chef d'Atelier
        </h1>
        <p className="text-gray-600 mt-1">Suivre l'avancement et valider les étapes de chaque fiche de fabrication</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Liste des OF */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-md border border-gray-200">
            <div className="p-4 border-b">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un OF..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:border-indigo-400 focus:outline-none"
                />
              </div>
            </div>

            <div className="overflow-y-auto max-h-[65vh]">
              {loading ? (
                <div className="p-6 text-center text-gray-400">Chargement...</div>
              ) : ordresFiltres.length === 0 ? (
                <div className="p-6 text-center text-gray-400">Aucun ordre en cours</div>
              ) : (
                ordresFiltres.map(ordre => (
                  <div
                    key={ordre.id}
                    onClick={() => ouvrirFiche(ordre)}
                    className={`p-4 border-b cursor-pointer hover:bg-indigo-50 transition-colors ${
                      selectedOrdre?.id === ordre.id ? 'bg-indigo-50 border-l-4 border-l-indigo-500' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-bold text-indigo-700">{ordre.numero_ordre}</div>
                        <div className="text-sm text-gray-600">{ordre.type_bijou} {ordre.couleur_or ? `— ${ordre.couleur_or}` : ''}</div>
                        {ordre.client_nom && (
                          <div className="text-xs text-gray-500 mt-0.5">{ordre.client_nom}</div>
                        )}
                      </div>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                    <div className="mt-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        ordre.statut === 'en_cours' ? 'bg-orange-100 text-orange-700' :
                        ordre.statut === 'en_attente' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {ordre.statut}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Détail fiche */}
        <div className="lg:col-span-2">
          {!selectedOrdre ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center h-64">
              <div className="text-center text-gray-400">
                <Eye size={48} className="mx-auto mb-3 opacity-30" />
                <p>Sélectionnez un ordre de fabrication</p>
              </div>
            </div>
          ) : ficheLoading ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 flex items-center justify-center h-64">
              <div className="text-center text-gray-400">Chargement de la fiche...</div>
            </div>
          ) : ficheData ? (
            <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
              {/* En-tête fiche */}
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-xl font-bold">{ficheData.ordre?.numero_ordre}</h2>
                    <p className="text-indigo-200 text-sm">{ficheData.ordre?.type_bijou} — {ficheData.ordre?.metal || ficheData.ordre?.couleur_or}</p>
                    {ficheData.ordre?.client_nom && (
                      <p className="text-indigo-100 text-sm mt-0.5">Client: {ficheData.ordre.client_nom}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      {etapesActives > 0 ? Math.round((etapesValidees / etapesActives) * 100) : 0}%
                    </div>
                    <div className="text-indigo-200 text-xs">validé</div>
                  </div>
                </div>
                {/* Barre de progression */}
                {totalEtapes > 0 && (
                  <div className="mt-3 bg-indigo-800 rounded-full h-2">
                    <div
                      className="bg-green-400 h-2 rounded-full transition-all"
                      style={{ width: `${etapesActives > 0 ? Math.round((etapesValidees / etapesActives) * 100) : 0}%` }}
                    />
                  </div>
                )}
              </div>

              {/* Alertes */}
              {etapesEnAttente > 0 && (
                <div className="bg-blue-50 border-b border-blue-200 px-5 py-3 flex items-center gap-2 text-blue-700 text-sm font-semibold">
                  <AlertCircle size={16} />
                  {etapesEnAttente} étape{etapesEnAttente > 1 ? 's' : ''} en attente de validation chef
                </div>
              )}

              {/* Étapes de production */}
              <div className="p-5">
                <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                  Étapes de fabrication
                  <span className="text-xs text-gray-500 font-normal">
                    ({etapesActives} lancée{etapesActives > 1 ? 's' : ''}{etapesNonDemarrees > 0 ? ` · ${etapesNonDemarrees} à planifier` : ''})
                  </span>
                </h3>

                {totalEtapes === 0 ? (
                  <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-lg">
                    <Clock size={32} className="mx-auto mb-2 opacity-40" />
                    <p className="text-sm">Aucune étape définie pour cet OF</p>
                    <p className="text-xs mt-1">Vérifiez que le devis contient des étapes de fabrication</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {ficheData.etapes_production.map((etape) => {
                      const style = getStatutStyle(etape.statut);
                      const peutValider = etape.statut === 'terminee';
                      return (
                        <div
                          key={etape.id || etape.nom_etape}
                          className={`rounded-lg border-2 p-4 ${
                            etape.in_devis === false
                              ? 'bg-orange-50 border-orange-300'
                              : `${style.bg} ${style.border}`
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {style.icon}
                              <div className="min-w-0">
                                <div className={`font-bold ${etape.in_devis === false ? 'text-orange-700' : style.text}`}>
                                  {etape.nom_etape}
                                  {etape.in_devis === false ? (
                                    <span className="ml-2 text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded font-normal">⚠ hors devis</span>
                                  ) : (
                                    <span className="ml-2 text-xs bg-green-600 text-white px-1.5 py-0.5 rounded font-normal">devis</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-xs text-gray-600">
                                  {etape.ouvrier_nom && (
                                    <span className="flex items-center gap-1">
                                      <User size={11} />
                                      {etape.ouvrier_nom} {etape.ouvrier_prenom || ''}
                                    </span>
                                  )}
                                  {etape.date_debut && (
                                    <span className="flex items-center gap-1">
                                      <Clock size={11} />
                                      {new Date(etape.date_debut).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                  {etape.date_fin && (
                                    <span className="flex items-center gap-1">
                                      <CheckCircle size={11} />
                                      fin {new Date(etape.date_fin).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 ml-4 shrink-0">
                              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${style.bg} ${style.text} border ${style.border}`}>
                                {style.label}
                              </span>
                              {peutValider && (
                                <button
                                  onClick={() => validerEtape(etape.id, etape.nom_etape)}
                                  disabled={validating === etape.id}
                                  className="flex items-center gap-1 bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
                                >
                                  <CheckCircle size={13} />
                                  {validating === etape.id ? '...' : 'Valider'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Bouton terminer OF */}
                {totalEtapes > 0 && (
                  <div className="mt-6 pt-4 border-t flex justify-end">
                    <button
                      onClick={terminerOF}
                      className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold hover:from-green-700 hover:to-emerald-700 shadow-lg"
                    >
                      <CheckCircle size={18} />
                      Bijou terminé — Passer au stock
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default SuiviFicheChef;
